export const DEFAULT_EVENT_TYPE = 'wedding';

export const EVENT_TYPE_CONFIG = {
  wedding: {
    id: 'wedding',
    label: 'Wedding',
    createEvent: {
      title: 'Wedding',
      description: 'Celebrate a wedding with guests, hosts, itinerary, photos, and stationery.',
      namePlaceholder: 'e.g., Wedding of John & Jane',
      descriptionPlaceholder: 'Tell your guests about the celebration...',
      subtitle: 'Set up your wedding or event',
    },
    labels: {
      appTagline: 'Your perfect wedding, perfectly managed',
      authDescription: 'Sign in to manage your wedding or create an account',
      guestJoinDescription: 'Join as a wedding guest',
      hostInviteCode: 'Couple Invite Code',
      hostInviteHelp: 'Share with bride/groom for special access',
      guestInviteHelp: 'Share with guests to let them join the event',
      hostRoleName: 'Couple',
      hostNameLabel: 'Couple Names',
      logoLabel: 'Couple Logo',
      logoDescription: 'Upload your couple logo or monogram to personalize your event',
      themeTitle: 'Wedding Theme',
      themeDescription: 'Customize your wedding theme and generate matching stationery',
      themePromptLabel: 'Describe Your Dream Theme',
      themePromptPlaceholder: 'e.g., Purple floral theme with peacocks, royal and elegant...',
      themePromptButton: 'Generate Theme Suggestions',
      imageUploadHint: 'Try uploading: wedding decor photos, fabric swatches, invitation designs, or any inspiration image',
      stationeryTitle: 'Generate Wedding Stationery',
      stationeryDescription: "Select the stationery items you'd like to generate based on your wedding theme.",
      stationeryThemeRequired: 'Please select a wedding theme first before generating stationery.',
      stationeryDetailsTitle: 'Couple Details',
      stationeryDetailsDescription: 'Enter the names to be displayed on your stationery',
      photoAlbumTab: 'Wedding Album',
      photoAlbumDescription: 'Official couple uploads and previews.',
      photoAlbumEmpty: 'Official wedding photos will be added here by the couple.',
      photoGalleryTitle: 'Photographer Gallery',
      photoGalleryDescription: 'View the complete professional album from the photographer.',
      guestHomeTitleFallback: 'Wedding Celebration',
      guestHomeBadge: 'Wedding Weekend',
      guestHomeStoryFallback: 'A joyful celebration of love, family, and shared moments.',
      guestFooterEyebrow: 'Wedding Weekend',
      guestFooterDescription: 'Everything your guests need, in one calm and beautiful place.',
      dashboardGuestModule: "Add and manage your wedding guests",
      settingsThemePreview: 'This is how your event pages will appear to guests. The colors, fonts, and decorative elements will be applied throughout all dashboards.',
      guestPlural: 'Guests',
      invitationSubject: 'wedding',
      registryTitle: "Couple's Wishlist",
      registryShortTitle: 'Wishlist',
      registryDescription: 'Explore thoughtful gifts for the couple.',
      registryRecipient: 'the couple',
      itineraryEyebrow: 'The wedding festivities',
      itineraryTitle: 'A celebration, beautifully planned',
      countdownLabel: 'Until We Celebrate Together',
      inviteStudioIntro: 'Create polished wedding invite media from event details and photos, then use the selected invite for WhatsApp sending.',
      inviteStudioEmptyTitle: 'Create beautiful wedding invites in minutes',
      fullEventInvite: 'Full wedding invite',
      socialPhotoStep: 'Choose the couple photo',
      socialPhotoEmpty: 'This event has no Guest View Hero couple photos. Upload one to generate a design.',
      socialNamesLabel: 'Couple names',
      socialGenerationDetail: 'Reading the selected photos, mood and wedding details',
      itineraryAddDescription: 'Add a new event to the wedding schedule',
      itineraryNamePlaceholder: 'e.g., Sangeet Night',
      heroPortraitLabel: 'Couple photo',
      heroPortraitAlt: 'Current couple',
      heroPortraitUpdated: 'Couple photo updated',
      heroPortraitUploadError: 'Could not upload the couple photo',
      heroManagerDescription: 'Manage the couple portrait and the photo or video slideshow shown at the top of the guest view.',
      themeViewAudience: 'guest and couple views',
      inviteFamilyLinePlaceholder: 'e.g. Sharma and Mehta families',
      giftTrackingTitle: 'Gift received',
      giftDescriptionPlaceholder: 'e.g., Silver dinner set',
      thankYouSenderPlaceholder: 'e.g., Aisha & Rohan',
    },
    showcase: {
      galleryCategories: ['All', 'Decor', 'Venue', 'Haldi', 'Mehendi', 'Sangeet', 'Wedding', 'Reception', 'Bride', 'Groom', 'BTS'],
      eventTags: ['Luxury', 'Destination', 'Beach', 'Palace', 'Intimate', 'Reception', 'Engagement', 'Traditional', 'Modern'],
      themePlaceholder: 'Royal garden wedding',
      testimonialPhotoLabel: 'Couple Photo',
      testimonialPrimaryNameLabel: 'Bride Name',
      testimonialSecondaryNameLabel: 'Groom Name',
      testimonialDateLabel: 'Wedding Date',
      journeyEyebrow: 'The couple journey',
      journeyTitle: 'From the first idea to the final farewell.',
      testimonialPhotoAlt: 'The couple',
      recognitionTitle: 'Trusted by couples. Recognized by the industry.',
      serviceOptions: ['Destination Weddings', 'Wedding Planning', 'Hospitality', 'Guest Management', 'RSVP Management', 'Airport Transfers', 'Transport Logistics', 'Hotel Management', 'Artist Management', 'Decoration', 'Venue Booking', 'Photography', 'Entertainment', 'Catering'],
    },
    features: {
      showHostInviteCode: true,
      showWishlist: true,
      showWardrobe: true,
      showWeddingAlbumLanguage: true,
      showCoupleDetailsInputs: true,
      allowThemeBranding: true,
      allowStationery: true,
      showAccommodation: true,
      showTravelAndId: true,
      showGuestSides: true,
      showSocialStudio: true,
      showInviteStudio: true,
      showGuestGiftTracking: true,
      showBabyShowerGames: false,
      showCelebrationGames: false,
      showManagerLocation: true,
      defaultItineraryEnabled: true,
      itineraryOptional: false,
      defaultAccommodationEnabled: true,
      accommodationOptional: false,
      defaultGuestRequiresRoom: true,
      allowedStationeryTypes: ['wedding_invitation', 'day_invitation', 'save_the_date', 'room_tag', 'table_number', 'menu_card', 'thank_you_card', 'rsvp_card', 'itinerary_card'],
    },
  },
  corporate: {
    id: 'corporate',
    label: 'Corporate',
    createEvent: {
      title: 'Corporate Event',
      description: 'Plan a conference, offsite, summit, meetup, or company celebration.',
      namePlaceholder: 'e.g., Annual Leadership Summit 2026',
      descriptionPlaceholder: 'Share the agenda, purpose, or attendee context...',
      subtitle: 'Set up your corporate event',
    },
    labels: {
      appTagline: 'Professional events, perfectly managed',
      authDescription: 'Sign in to manage your events or create an account',
      guestJoinDescription: 'Join as an event attendee',
      hostInviteCode: 'Host Invite Code',
      hostInviteHelp: 'Special host access is disabled for this event type.',
      guestInviteHelp: 'Share with attendees to let them join the event',
      hostRoleName: 'Hosts',
      hostNameLabel: 'Host Details',
      logoLabel: 'Event Logo',
      logoDescription: 'Upload an event logo or brand mark to personalize your event',
      themeTitle: 'Event Theme',
      themeDescription: 'Customize your event branding and generate matching materials',
      themePromptLabel: 'Describe Your Event Theme',
      themePromptPlaceholder: 'e.g., Modern leadership summit with clean typography, navy accents, and premium branding...',
      themePromptButton: 'Generate Event Theme Suggestions',
      imageUploadHint: 'Try uploading: brand decks, booth designs, signage, decor concepts, or any inspiration image',
      stationeryTitle: 'Generate Event Materials',
      stationeryDescription: "Select the branded materials you'd like to generate for this event.",
      stationeryThemeRequired: 'Please select an event theme first before generating materials.',
      stationeryDetailsTitle: 'Event Details',
      stationeryDetailsDescription: 'Event details will be used automatically for the selected materials',
      photoAlbumTab: 'Featured Album',
      photoAlbumDescription: 'Featured organizer uploads and previews.',
      photoAlbumEmpty: 'Featured event photos will be added here by the organizers.',
      photoGalleryTitle: 'Event Gallery',
      photoGalleryDescription: 'View the complete event gallery from the organizers or photographer.',
      guestHomeTitleFallback: 'Corporate Event',
      guestHomeBadge: 'Event Access',
      guestHomeStoryFallback: 'A well-organized experience for attendees, speakers, and teams.',
      guestFooterEyebrow: 'Event Access',
      guestFooterDescription: 'Everything attendees need, in one simple and polished place.',
      dashboardGuestModule: 'Add and manage attendees for your event',
      settingsThemePreview: 'This is how your event pages will appear to attendees. The colors, fonts, and decorative elements will be applied throughout all dashboards.',
      guestPlural: 'Attendees',
      invitationSubject: 'event',
      registryTitle: 'Event Registry',
      registryShortTitle: 'Registry',
      registryDescription: 'View items and resources shared by the organizers.',
      registryRecipient: 'the organizers',
      itineraryEyebrow: 'The event schedule',
      itineraryTitle: 'Your event, clearly organized',
      countdownLabel: 'Until The Event Begins',
      inviteStudioIntro: 'Create polished event invite media from event details and photos, then use the selected invite for WhatsApp sending.',
      inviteStudioEmptyTitle: 'Create beautiful event invites in minutes',
      fullEventInvite: 'Full event invite',
      socialPhotoStep: 'Choose the event photo',
      socialPhotoEmpty: 'This event has no featured Guest View photos. Upload one to generate a design.',
      socialNamesLabel: 'Host names',
      socialGenerationDetail: 'Reading the selected photos, mood and event details',
      itineraryAddDescription: 'Add a new item to the event schedule',
      itineraryNamePlaceholder: 'e.g., Opening Keynote',
      heroPortraitLabel: 'Featured event photo',
      heroPortraitAlt: 'Current featured event photo',
      heroPortraitUpdated: 'Featured event photo updated',
      heroPortraitUploadError: 'Could not upload the featured event photo',
      heroManagerDescription: 'Manage the featured event photo and the photo or video slideshow shown at the top of the attendee view.',
      themeViewAudience: 'attendee and host views',
      inviteFamilyLinePlaceholder: 'e.g. Hosted by Acme Events',
      giftTrackingTitle: 'Received item',
      giftDescriptionPlaceholder: 'e.g., Event memento',
      thankYouSenderPlaceholder: 'e.g., The organizing team',
    },
    showcase: {
      galleryCategories: ['All', 'Decor', 'Venue', 'Stage', 'Sessions', 'Speakers', 'Networking', 'Team', 'Guests', 'BTS'],
      eventTags: ['Leadership', 'Conference', 'Offsite', 'Summit', 'Networking', 'Launch', 'Festival', 'Modern'],
      themePlaceholder: 'Modern leadership summit',
      testimonialPhotoLabel: 'Client or Team Photo',
      testimonialPrimaryNameLabel: 'Contact Name',
      testimonialSecondaryNameLabel: 'Organization or Co-host',
      testimonialDateLabel: 'Event Date',
      journeyEyebrow: 'The event journey',
      journeyTitle: 'From the first brief to the final session.',
      testimonialPhotoAlt: 'Event client or team',
      recognitionTitle: 'Trusted by clients. Recognized by the industry.',
      serviceOptions: ['Corporate Events', 'Hospitality', 'Guest Management', 'RSVP Management', 'Airport Transfers', 'Transport Logistics', 'Hotel Management', 'Artist Management', 'Decoration', 'Venue Booking', 'Photography', 'Entertainment', 'Catering', 'Venue Branding', 'Hospitality Desk'],
    },
    features: {
      showHostInviteCode: false,
      showWishlist: false,
      showWardrobe: false,
      showWeddingAlbumLanguage: false,
      showCoupleDetailsInputs: false,
      allowThemeBranding: true,
      allowStationery: true,
      showAccommodation: true,
      showTravelAndId: true,
      showGuestSides: false,
      showSocialStudio: true,
      showInviteStudio: true,
      showGuestGiftTracking: false,
      showBabyShowerGames: false,
      showCelebrationGames: false,
      showManagerLocation: true,
      defaultItineraryEnabled: true,
      itineraryOptional: false,
      defaultAccommodationEnabled: true,
      accommodationOptional: false,
      defaultGuestRequiresRoom: false,
      allowedStationeryTypes: ['room_tag', 'table_number', 'menu_card', 'rsvp_card', 'itinerary_card'],
    },
  },
  baby_shower: {
    id: 'baby_shower',
    label: 'Baby Shower',
    createEvent: {
      title: 'Baby Shower',
      description: 'Plan a warm baby shower with invitations, guests, a registry, photos, and joyful moments.',
      namePlaceholder: 'e.g., A Baby Shower for Aisha',
      descriptionPlaceholder: 'Share a welcome note or tell guests about the celebration...',
      subtitle: 'Set up your baby shower',
    },
    labels: {
      appTagline: 'Joyful beginnings, beautifully celebrated',
      authDescription: 'Sign in to manage your baby shower or join as a guest',
      guestJoinDescription: 'Join as a baby shower guest',
      hostInviteCode: 'Host Invite',
      hostInviteHelp: 'Invite parents-to-be or trusted hosts to help manage the celebration',
      guestInviteHelp: 'Share with guests to let them join the baby shower',
      hostRoleName: 'Host',
      hostNameLabel: 'Host Names',
      logoLabel: 'Celebration Logo',
      logoDescription: 'Upload a baby shower logo or family monogram to personalize the celebration',
      themeTitle: 'Baby Shower Theme',
      themeDescription: 'Customize the baby shower theme and generate matching invitations and stationery',
      themePromptLabel: 'Describe Your Baby Shower Theme',
      themePromptPlaceholder: 'e.g., Soft woodland storybook theme with sage green, warm cream, and playful animals...',
      themePromptButton: 'Generate Baby Shower Themes',
      imageUploadHint: 'Try uploading: nursery inspiration, floral arrangements, fabric swatches, invitation designs, or decor photos',
      stationeryTitle: 'Generate Baby Shower Stationery',
      stationeryDescription: "Select the invitations and celebration materials you'd like to generate from your baby shower theme.",
      stationeryThemeRequired: 'Please select a baby shower theme before generating stationery.',
      stationeryDetailsTitle: 'Celebration Details',
      stationeryDetailsDescription: 'Host and event details will be used on the selected materials',
      photoAlbumTab: 'Baby Shower Album',
      photoAlbumDescription: 'Featured host uploads and celebration previews.',
      photoAlbumEmpty: 'Baby shower photos will be added here by the hosts.',
      photoGalleryTitle: 'Baby Shower Gallery',
      photoGalleryDescription: 'View the complete celebration gallery from the hosts or photographer.',
      guestHomeTitleFallback: 'Baby Shower Celebration',
      guestHomeBadge: 'A Sweet Celebration',
      guestHomeStoryFallback: 'A joyful gathering to celebrate a growing family and the little one on the way.',
      guestFooterEyebrow: 'Baby Shower',
      guestFooterDescription: 'Everything guests need for this joyful celebration, all in one place.',
      dashboardGuestModule: 'Add and manage baby shower guests',
      settingsThemePreview: 'This is how the baby shower pages will appear to guests. The colors, fonts, and decorative elements will be applied throughout the experience.',
      guestPlural: 'Guests',
      invitationSubject: 'baby shower',
      registryTitle: 'Baby Registry',
      registryShortTitle: 'Baby Registry',
      registryDescription: 'Explore thoughtful gifts selected for the baby and growing family.',
      registryRecipient: 'the growing family',
      itineraryEyebrow: 'The baby shower moments',
      itineraryTitle: 'A joyful celebration, beautifully planned',
      countdownLabel: 'Until The Baby Shower',
      inviteStudioIntro: 'Create polished baby shower invite media from event details and photos, then use the selected invite for WhatsApp sending.',
      inviteStudioEmptyTitle: 'Create beautiful baby shower invites in minutes',
      fullEventInvite: 'Full baby shower invite',
      socialPhotoStep: 'Choose the celebration photo',
      socialPhotoEmpty: 'This baby shower has no featured Guest View photos. Upload one to generate a design.',
      socialNamesLabel: 'Host names',
      socialGenerationDetail: 'Reading the selected photos, mood and baby shower details',
      itineraryAddDescription: 'Add a new moment to the baby shower schedule',
      itineraryNamePlaceholder: 'e.g., Welcome Brunch',
      heroPortraitLabel: 'Featured celebration photo',
      heroPortraitAlt: 'Current featured celebration photo',
      heroPortraitUpdated: 'Featured celebration photo updated',
      heroPortraitUploadError: 'Could not upload the featured celebration photo',
      heroManagerDescription: 'Manage the featured celebration photo and the photo or video slideshow shown at the top of the guest view.',
      themeViewAudience: 'guest and host views',
      inviteFamilyLinePlaceholder: 'e.g. Hosted by the Sharma family',
      giftTrackingTitle: 'Gift received',
      giftDescriptionPlaceholder: 'e.g., Baby blanket or stroller',
      thankYouSenderPlaceholder: 'e.g., Aisha & family',
      gameStudioTitle: 'Baby Shower Game Studio',
      celebrationStudioTitle: 'Baby Shower Game Studio',
      celebrationStudioKicker: 'Baby shower activities',
    },
    showcase: {
      galleryCategories: ['All', 'Decor', 'Venue', 'Baby Shower', 'Games', 'Gifts', 'Family', 'Parents-to-be', 'Guests', 'BTS'],
      eventTags: ['Joyful', 'Storybook', 'Woodland', 'Floral', 'Pastel', 'Intimate', 'Traditional', 'Modern'],
      themePlaceholder: 'Soft woodland baby shower',
      testimonialPhotoLabel: 'Host or Family Photo',
      testimonialPrimaryNameLabel: 'Parent or Host Name',
      testimonialSecondaryNameLabel: 'Co-host Name',
      testimonialDateLabel: 'Baby Shower Date',
      journeyEyebrow: 'The celebration journey',
      journeyTitle: 'From the first idea to the final joyful moment.',
      testimonialPhotoAlt: 'Baby shower hosts or family',
      recognitionTitle: 'Trusted by families and hosts. Recognized by the industry.',
      serviceOptions: ['Baby Shower Planning', 'Hospitality', 'Guest Management', 'RSVP Management', 'Decoration', 'Venue Booking', 'Photography', 'Entertainment', 'Catering'],
    },
    features: {
      showHostInviteCode: true,
      showWishlist: true,
      showWardrobe: false,
      showWeddingAlbumLanguage: false,
      showCoupleDetailsInputs: false,
      allowThemeBranding: true,
      allowStationery: true,
      showAccommodation: false,
      showTravelAndId: false,
      showGuestSides: false,
      showSocialStudio: true,
      showInviteStudio: true,
      showGuestGiftTracking: true,
      showBabyShowerGames: true,
      showCelebrationGames: true,
      showManagerLocation: false,
      defaultItineraryEnabled: false,
      itineraryOptional: true,
      defaultAccommodationEnabled: false,
      accommodationOptional: true,
      defaultGuestRequiresRoom: false,
      allowedStationeryTypes: ['baby_shower_invitation', 'save_the_date', 'thank_you_card', 'rsvp_card', 'menu_card', 'itinerary_card'],
    },
  },
  birthday: {
    id: 'birthday',
    label: 'Birthday',
    createEvent: {
      title: 'Birthday',
      description: 'Plan a memorable birthday with invitations, guests, activities, gifts, photos, and joyful moments.',
      namePlaceholder: "e.g., Aarav's 10th Birthday",
      descriptionPlaceholder: 'Share a welcome note or tell guests about the celebration...',
      subtitle: 'Set up your birthday celebration',
    },
    labels: {
      appTagline: 'Every year deserves a beautiful celebration',
      authDescription: 'Sign in to manage a birthday or join as a guest',
      guestJoinDescription: 'Join as a birthday guest',
      hostInviteCode: 'Host Invite',
      hostInviteHelp: 'Invite family or trusted hosts to help manage the celebration',
      guestInviteHelp: 'Share with guests to let them join the birthday celebration',
      hostRoleName: 'Host',
      hostNameLabel: 'Host Names',
      honoreeNameLabel: 'Who are we celebrating?',
      logoLabel: 'Celebration Logo',
      logoDescription: 'Upload a birthday logo or monogram to personalize the celebration',
      themeTitle: 'Birthday Theme',
      themeDescription: 'Customize the birthday theme and generate matching invitations and stationery',
      themePromptLabel: 'Describe Your Birthday Theme',
      themePromptPlaceholder: 'e.g., Colourful space adventure with deep blue, silver stars, and playful rockets...',
      themePromptButton: 'Generate Birthday Themes',
      imageUploadHint: 'Try uploading: party decor, colour palettes, invitation designs, cake inspiration, or favourite characters',
      stationeryTitle: 'Generate Birthday Stationery',
      stationeryDescription: "Select the invitations and celebration materials you'd like to generate from your birthday theme.",
      stationeryThemeRequired: 'Please select a birthday theme before generating stationery.',
      stationeryDetailsTitle: 'Celebration Details',
      stationeryDetailsDescription: 'Celebrant, host, and event details will be used on the selected materials',
      photoAlbumTab: 'Birthday Album',
      photoAlbumDescription: 'Featured host uploads and celebration previews.',
      photoAlbumEmpty: 'Birthday photos will be added here by the hosts.',
      photoGalleryTitle: 'Birthday Gallery',
      photoGalleryDescription: 'View the complete celebration gallery from the hosts or photographer.',
      guestHomeTitleFallback: 'Birthday Celebration',
      guestHomeBadge: "You're Invited",
      guestHomeStoryFallback: 'A joyful gathering for another wonderful year and the memories still to come.',
      guestFooterEyebrow: 'Birthday Celebration',
      guestFooterDescription: 'Everything guests need for the celebration, all in one place.',
      dashboardGuestModule: 'Add and manage birthday guests',
      settingsThemePreview: 'This is how the birthday pages will appear to guests. The colors, fonts, and decorative elements will be applied throughout the experience.',
      guestPlural: 'Guests',
      invitationSubject: 'birthday',
      registryTitle: 'Birthday Wishlist',
      registryShortTitle: 'Wishlist',
      registryDescription: 'Explore gift ideas selected for the celebrant.',
      registryRecipient: 'the celebrant',
      itineraryEyebrow: 'The birthday moments',
      itineraryTitle: 'A joyful celebration, beautifully planned',
      countdownLabel: 'Until The Birthday Celebration',
      inviteStudioIntro: 'Create polished birthday invite media from event details and photos, then use the selected invite for WhatsApp sending.',
      inviteStudioEmptyTitle: 'Create beautiful birthday invites in minutes',
      fullEventInvite: 'Full birthday invite',
      socialPhotoStep: 'Choose the celebrant photo',
      socialPhotoEmpty: 'This birthday has no featured Guest View photos. Upload one to generate a design.',
      socialNamesLabel: 'Celebrant names',
      socialGenerationDetail: 'Reading the selected photos, mood and birthday details',
      itineraryAddDescription: 'Add a new moment to the birthday schedule',
      itineraryNamePlaceholder: 'e.g., Cake Cutting',
      heroPortraitLabel: 'Celebrant photo',
      heroPortraitAlt: 'Current celebrant',
      heroPortraitUpdated: 'Celebrant photo updated',
      heroPortraitUploadError: 'Could not upload the celebrant photo',
      heroManagerDescription: 'Manage the celebrant portrait and the photo or video slideshow shown at the top of the guest view.',
      themeViewAudience: 'guest and host views',
      inviteFamilyLinePlaceholder: 'e.g. Hosted by the Mehta family',
      giftTrackingTitle: 'Gift received',
      giftDescriptionPlaceholder: 'e.g., Art set or favourite book',
      thankYouSenderPlaceholder: 'e.g., Aarav & family',
      gameStudioTitle: 'Birthday Activity Studio',
      celebrationStudioTitle: 'Birthday Activity Studio',
      celebrationStudioKicker: 'Birthday activities',
    },
    showcase: {
      galleryCategories: ['All', 'Decor', 'Cake', 'Games', 'Gifts', 'Family', 'Friends', 'Guests', 'BTS'],
      eventTags: ['Joyful', 'Milestone', 'Colourful', 'Themed', 'Intimate', 'Elegant', 'Playful', 'Modern'],
      themePlaceholder: 'Colourful space birthday',
      testimonialPhotoLabel: 'Celebrant or Family Photo',
      testimonialPrimaryNameLabel: 'Celebrant Name',
      testimonialSecondaryNameLabel: 'Host Name',
      testimonialDateLabel: 'Birthday Date',
      journeyEyebrow: 'The celebration journey',
      journeyTitle: 'From the first idea to the final birthday wish.',
      testimonialPhotoAlt: 'Birthday celebrant or family',
      recognitionTitle: 'Trusted by families and hosts. Recognized by the industry.',
      serviceOptions: ['Birthday Planning', 'Hospitality', 'Guest Management', 'RSVP Management', 'Decoration', 'Venue Booking', 'Photography', 'Entertainment', 'Catering'],
    },
    features: {
      showHostInviteCode: true,
      showWishlist: true,
      showWardrobe: false,
      showWeddingAlbumLanguage: false,
      showCoupleDetailsInputs: false,
      allowThemeBranding: true,
      allowStationery: true,
      showAccommodation: false,
      showTravelAndId: false,
      showGuestSides: false,
      showSocialStudio: true,
      showInviteStudio: true,
      showGuestGiftTracking: true,
      showBabyShowerGames: false,
      showCelebrationGames: true,
      showManagerLocation: false,
      defaultItineraryEnabled: false,
      itineraryOptional: true,
      defaultAccommodationEnabled: false,
      accommodationOptional: true,
      defaultGuestRequiresRoom: false,
      allowedStationeryTypes: ['birthday_invitation', 'save_the_date', 'thank_you_card', 'rsvp_card', 'menu_card', 'itinerary_card'],
    },
  },
};

export function getEventTypeId(eventType) {
  return EVENT_TYPE_CONFIG[eventType] ? eventType : DEFAULT_EVENT_TYPE;
}

export function getEventTypeConfig(eventType) {
  return EVENT_TYPE_CONFIG[getEventTypeId(eventType)];
}

export function isEventFeatureEnabled(event, featureName) {
  const config = getEventTypeConfig(event?.event_type);
  if (featureName === 'showItinerary') {
    return typeof event?.itinerary_enabled === 'boolean'
      ? event.itinerary_enabled
      : Boolean(config.features.defaultItineraryEnabled);
  }
  if (featureName === 'showAccommodation') {
    return typeof event?.accommodation_enabled === 'boolean'
      ? event.accommodation_enabled
      : Boolean(config.features.defaultAccommodationEnabled ?? config.features.showAccommodation);
  }
  return Boolean(config.features?.[featureName]);
}

export function getSupportedEventTypes() {
  return Object.values(EVENT_TYPE_CONFIG);
}
